import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Gauge } from 'lucide-react';
import { parseUtc } from '../../lib/parseUtc';

/**
 * UtilizationBars — the 3-layer Provisioned vs Requested vs Used comparison.
 *
 * The single most legible cost-story device in the category (CloudPilot/CAST
 * render a variant of it on every view): over-provisioning is not a number in
 * a tooltip, it is the VISIBLE GAP between three stacked bars —
 *
 *   Provisioned  ████████████████████████  32 cores   (node capacity you pay for)
 *   Requested    ████████████░░░░░░░░░░░░  17.5 cores (what pods reserve)
 *   Used         █████░░░░░░░░░░░░░░░░░░░   6.2 cores (what pods actually burn)
 *
 * Data: GET /finops/monitoring/{id} → .live (ClickHouse, single source):
 *   cpu_capacity_cores / cpu_requested_cores / cpu_used_cores
 *   mem_capacity_gb    / mem_requested_gb    / mem_used_gb     + timestamp.
 *
 * HONEST-EMPTY contract (house discipline: dashes, never stale numbers): the
 * backend returns live={} when pod metrics are absent or older than the
 * freshness window (~30 min) — this component then renders an explicit
 * "no fresh agent metrics" state with em-dashes. It NEVER substitutes cached
 * or estimated values, and shows the sample timestamp when data IS fresh.
 */

const pct = (part, whole) => (whole > 0 ? Math.min(100, (part / whole) * 100) : 0);
const fmtPct = (part, whole) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—');

// One horizontal layer: label, bar, value. `tone` picks the fill colour.
const Layer = ({ label, value, unit, frac, tone, hint }) => {
  const tones = {
    slate: 'bg-slate-400 dark:bg-slate-500',
    blue: 'bg-blue-500 dark:bg-blue-400',
    emerald: 'bg-emerald-500 dark:bg-emerald-400',
  };
  return (
    <div className="flex items-center gap-3" title={hint}>
      <span className="w-24 shrink-0 text-xs text-slate-500 dark:text-slate-400">{label}</span>
      <div className="flex-1 h-3.5 rounded bg-slate-100 dark:bg-slate-800 overflow-hidden">
        <div className={`h-full rounded ${tones[tone]}`} style={{ width: `${frac}%` }} />
      </div>
      <span className="w-28 shrink-0 text-right text-xs font-medium tabular-nums text-slate-700 dark:text-slate-200">
        {value == null ? '—' : `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} ${unit}`}
      </span>
    </div>
  );
};

// One resource group (CPU or Memory): the three layers + the utilization chips.
const ResourceGroup = ({ title, unit, provisioned, requested, used }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200">{title}</h4>
      <div className="flex gap-1.5">
        <Badge className="bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 text-[10px]" title={`Requested / Provisioned — how much of the capacity you pay for is even reserved`}>
          req {fmtPct(requested, provisioned)}
        </Badge>
        <Badge className="bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 text-[10px]" title={`Used / Provisioned — how much of the capacity you pay for is actually burned`}>
          used {fmtPct(used, provisioned)}
        </Badge>
      </div>
    </div>
    <Layer label="Provisioned" value={provisioned} unit={unit} frac={100} tone="slate"
      hint="Node capacity you pay for (sum of node allocatable)" />
    <Layer label="Requested" value={requested} unit={unit} frac={pct(requested, provisioned)} tone="blue"
      hint="Sum of pod resource requests — what the scheduler has reserved" />
    <Layer label="Used" value={used} unit={unit} frac={pct(used, provisioned)} tone="emerald"
      hint="Live usage — what pods actually consume right now" />
  </div>
);

const UtilizationBars = ({ clusterId }) => {
  const [live, setLive] = useState(null);   // null = loading; {} = no fresh data
  useEffect(() => {
    if (!clusterId) return;
    let dead = false;
    axios.get(`${API}/finops/monitoring/${clusterId}?hours=1`)
      .then((r) => { if (!dead) setLive(r.data?.live || {}); })
      .catch(() => { if (!dead) setLive({}); });
    return () => { dead = true; };
  }, [clusterId]);

  const hasData = live && live.cpu_capacity_cores > 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Gauge className="h-4 w-4 text-slate-400" />
            Resource Utilization
            <span className="text-xs font-normal text-slate-400">provisioned vs requested vs used</span>
          </CardTitle>
          {hasData && live.timestamp ? (
            <Badge className="bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 text-[10px]">
              fresh · {parseUtc(live.timestamp).toLocaleTimeString()}
            </Badge>
          ) : live !== null ? (
            <Badge className="bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 text-[10px]">
              no fresh data
            </Badge>
          ) : null}
        </div>
      </CardHeader>
      <CardContent>
        {live === null ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : !hasData ? (
          // Honest-empty: the backend refuses to serve stale metrics (>30 min);
          // so do we. Dashes, not yesterday's numbers dressed up as live.
          <p className="text-sm text-slate-500 dark:text-slate-400">
            No fresh agent metrics (sample older than the freshness window, or the
            agent isn&apos;t reporting). Utilization renders only from live data — never
            from stale samples.
          </p>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            <ResourceGroup title="CPU" unit="cores"
              provisioned={live.cpu_capacity_cores}
              requested={live.cpu_requested_cores}
              used={live.cpu_used_cores} />
            <ResourceGroup title="Memory" unit="GB"
              provisioned={live.mem_capacity_gb}
              requested={live.mem_requested_gb}
              used={live.mem_used_gb} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UtilizationBars;
